<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="q-py-md">
        <template v-if="ordersItems.length >= 1">
            <q-table
                :data="ordersItems"
                :columns="columns"
                :pagination.sync="pagination"
                :rows-per-page-options="rowsPerPageOptions"
                :loading="isFetchingClientCourses"
                binary-state-sort
                row-key="id"
                flat
                @request="request"
            >
                <!-- course column -->
                <template v-slot:body-cell-course="props">
                    <q-td key="course" :props="props">
                        {{
                            props.row.courses
                                ? JSONParseTranslations(
                                      props.row.courses.title,
                                      locale.key
                                  )
                                : props.row.course_id
                        }}
                    </q-td>
                </template>
                <!-- used column -->
                <template v-slot:body-cell-used="props">
                    <q-td key="used" :props="props">
                        {{ props.row.libraries.length }}
                    </q-td>
                </template>
                <!-- assign column -->
                <template v-slot:body-cell-assign="props">
                    <q-td key="assign" :props="props">
                        <q-btn
                            v-if="
                                props.row.courses.courses_coursetype.slug ==
                                slugs.courseTypes.online
                            "
                            class="q-px-sm"
                            color="secondary"
                            :label="$t('general.assign')"
                            dense
                            unelevated
                            :disable="
                                !studentRoleId ||
                                !coursesAvailableToGive(props.row)
                            "
                            @click="showDialog(props.row)"
                        />
                        <q-icon v-else name="info" size="30px">
                            <q-tooltip>{{
                                $t('general.contact_cfma_to_register_trainees')
                            }}</q-tooltip>
                        </q-icon>
                    </q-td>
                </template>
            </q-table>
            <AssignEmployee
                :show="dialog.show"
                :client="user"
                :order-item="orderItem"
                :role-id="studentRoleId"
                @toggle-dialog="toggleDialog"
                @refresh="refresh"
            />
        </template>
        <template v-else>
            <h6>{{ $t('profile.courses_none') }}</h6>
            <q-btn class="q-my-md" to="/courses" color="secondary">
                {{ $t('general.view_course_catalog') }}
            </q-btn>
        </template>
    </div>
</template>

<script>
import AssignEmployee from '@/components/AssignEmployee'
import translate from '@/local-shared/mixins/translate'
import ordersService from '@/services/modules/orders'
import PageLoading from '@/components/PageLoading'
import store from '@/store'
import { slugs } from '@/local-shared/mixins/slugs'
import rolesService from '@/services/modules/roles'

const loadingKeys = {
    fetchingClientCourses: 'fetching-client-courses',
    fetchingRoles: 'fetching-roles',
}

export default {
    name: 'OurCourses',

    components: {
        PageLoading,
        AssignEmployee,
    },

    mixins: [translate],

    data() {
        return {
            currentPage: 1,

            coursesStock: [],
            ordersItems: [],

            pagination: {
                sortBy: 'id',
                descending: true,
                page: 1,
                rowsPerPage: 10,
                rowsNumber: 0,
            },

            rowsPerPageOptions: [5, 10, 25, 50, 100],

            dialog: {
                show: false,
            },

            orderItem: {},

            slugs: slugs,

            studentRoleId: null,
        }
    },

    computed: {
        columns() {
            return [
                {
                    name: 'order_id',
                    label: this.$t('general.order_id'),
                    field: 'order_id',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'course',
                    label: this.$t('general.course'),
                    field: 'course',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'quantity',
                    label: this.$t('general.boughted'),
                    field: 'quantity',
                    align: 'right',
                    sortable: true,
                },
                {
                    name: 'used',
                    label: this.$t('general.used'),
                    field: 'quantity',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'assign',
                    align: 'center',
                    label: this.$t('general.assign'),
                    field: 'assign',
                    sortable: false,
                },
            ]
        },

        isLoading() {
            return this.isFetchingClientCourses
        },

        isFetchingClientCourses() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingClientCourses
            ).length
        },

        locale() {
            return store.getters['locale/currentLocale']
        },

        user() {
            return store.getters['session/user']
        },
    },

    async created() {
        await this.fetchRoles()
        await this.fetchClientCourses()
    },

    methods: {
        coursesAvailableToGive(row) {
            return row.quantity - row.libraries.length > 0
        },

        mapColumn(value) {
            return value == null ? this.pagination.sortBy : value
        },

        mapOrder(value) {
            return value == true ? 'desc' : 'asc'
        },

        request(props) {
            const { page, rowsPerPage, sortBy, descending } = props.pagination

            this.pagination.page = page
            this.pagination.rowsPerPage = rowsPerPage
            this.pagination.sortBy = sortBy
            this.pagination.descending = descending

            this.fetchClientCourses()
        },

        async fetchClientCourses() {
            this.loadingAdd(loadingKeys.fetchingClientCourses)

            let parameters = {
                filter: {
                    clientId: this.user.affiliations[0].client_id,
                    orderStatusSlug: 'finalizada',
                },
                pagination: {
                    perPage: this.pagination.rowsPerPage,
                    currentPage: this.pagination.page,
                },
                orderBy: [
                    {
                        column: this.mapColumn(this.pagination.sortBy),
                        direction: this.mapOrder(this.pagination.descending),
                    },
                ],
                relations: {
                    courses: true,
                    inLibraries: true,
                },
            }

            let response = await ordersService.getItems(parameters)

            if (response.status === 200) {
                this.ordersItems = response.data.results
                this.pagination.rowsNumber = response.data.pagination.total
            } else {
                this.notifyError(response.data.message)
            }

            this.loadingRemove(loadingKeys.fetchingClientCourses)
        },

        showDialog(row) {
            this.orderItem = row
            this.dialog.show = true
        },

        toggleDialog() {
            this.dialog.show = !this.dialog.show
        },

        async refresh(payload) {
            this.dialog.show = false

            await this.fetchClientCourses()
            let orderItem = this.ordersItems.filter(
                (orderItem) => orderItem.id === payload.orderItemId
            )

            if (orderItem.length == 1) {
                this.showDialog(orderItem[0])
            }
        },

        async fetchRoles() {
            this.loadingAdd(loadingKeys.fetchingRoles)

            let parameters = {
                filter: {
                    name: 'student',
                },
            }

            let response = await rolesService.retrieve(parameters)

            if (
                response.status === 200 &&
                response.data.results &&
                response.data.results.length === 1
            ) {
                this.role = response.data.results
                this.studentRoleId = this.role[0].id
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingRoles)
        },
    },
}
</script>
